// #NAME? repair. When a workbook is what starts Excel, Excel may calculate it
// before the add-in has registered its functions (a file last saved by another
// Excel version is recalculated in full as it opens): every EAV formula then
// shows #NAME?, and stays so once the functions exist. Recalculating does not
// bring them back (range.calculate(), measured on Market Dashboard (EAV).xlsx,
// 2026-11-02); entering the formula again does. So, as the add-in starts in a
// workbook, and twice more over the next minute (that first calculation may
// still be running), the EAV formulas stuck on #NAME? are entered again, as
// they are written, each once. Other formulas showing #NAME? (a typo, an
// unknown function) are left alone.
//
// Such a cell reads `#BUSY` through range.values, not #NAME?: Excel holds it
// as computing (valuesAsJson errorType "Busy") with the #NAME? it shows as its
// previousValue, and it never finishes (measured 2026-11-02). valuesAsJson
// (ExcelApi 1.16) tells it apart whatever the display language (#NOM? in
// French); without it, a plain "#NAME?" value is the only sign.
//
// The pass only talks to a NameRepairPort, so it is tested without Excel.

import type { ScopedFormula } from "../lib/excelPort";

/** When the passes run, in ms after the add-in starts in the workbook. */
export const REPAIR_DELAYS_MS = [0, 10_000, 60_000];

/** Cells entered per sync: a large sheet is written in batches. */
const BATCH = 500;

/** A cell value as valuesAsJson gives it (the fields read here). */
interface JsonValue {
  type?: string;
  errorType?: string;
  previousValue?: JsonValue;
}

/**
 * Whether a cell is stuck on #NAME?: its valuesAsJson says #NAME?, or computing
 * with #NAME? still shown (previousValue); a range.values string is #NAME? itself.
 */
export function stuckOnName(value: unknown): boolean {
  if (typeof value === "string") return value === "#NAME?";
  const v = value as JsonValue | null;
  if (!v || typeof v !== "object") return false;
  return v.errorType === "Name" || (v.errorType === "Busy" && v.previousValue?.errorType === "Name");
}

/** The Excel side of the repair. */
export interface NameRepairPort {
  /** The workbook's formula cells stuck on #NAME?, with their formulas. */
  nameErrors(): Promise<ScopedFormula[]>;
  /** Enter each cell's formula again; the number of cells Excel refused. */
  reenter(cells: ScopedFormula[]): Promise<number>;
}

const key = (c: ScopedFormula) => `${c.sheet}\u0000${c.row}\u0000${c.col}\u0000${c.formula}`;

/**
 * One pass: the EAV formulas stuck on #NAME? are entered again. `done` keeps
 * the cells entered by earlier passes, which are not entered twice (one still
 * computing looks the same: busy, #NAME? before).
 */
export async function repairNameErrors(
  port: NameRepairPort,
  isEav: (formula: string) => boolean,
  done: Set<string> = new Set(),
): Promise<{ found: number; refused: number }> {
  const cells = (await port.nameErrors()).filter((c) => isEav(c.formula) && !done.has(key(c)));
  if (!cells.length) return { found: 0, refused: 0 };
  cells.forEach((c) => done.add(key(c)));
  return { found: cells.length, refused: await port.reenter(cells) };
}

/**
 * Run `pass` at each of `delays`. A pass due while the previous one still runs
 * (a large sheet takes minutes) is skipped; one that fails is logged and the
 * next still runs.
 */
export function scheduleNameRepair(pass: () => Promise<{ found: number; refused: number }>, delays: number[] = REPAIR_DELAYS_MS): void {
  let running = false;
  for (const ms of delays) {
    setTimeout(() => {
      if (running) return;
      running = true;
      pass()
        .then(({ found, refused }) => {
          if (found) console.info(`EAV: ${found} formula(s) stuck on #NAME? entered again${refused ? `, ${refused} refused by Excel` : ""}`);
        })
        .catch((e) => console.warn("EAV: #NAME? repair failed", e))
        .then(() => (running = false));
    }, ms);
  }
}

/**
 * NameRepairPort on Office.js. Only the formula cells showing an error are
 * read (getSpecialCells, ExcelApi 1.9: about 10 s for a sheet of a million used
 * cells, 25,000 of them errors), with valuesAsJson where the host has it.
 * Cells are written in batches.
 */
export const excelNameRepairPort: NameRepairPort = {
  nameErrors: () =>
    Excel.run(async (context) => {
      const json = Office.context.requirements.isSetSupported("ExcelApi", "1.16");
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const found = sheets.items.map((ws) => ({
        name: ws.name,
        areas: ws.getUsedRangeOrNullObject(true).getSpecialCellsOrNullObject(Excel.SpecialCellType.formulas, Excel.SpecialCellValueType.errors),
      }));
      await context.sync();
      const loaded = found.filter((f) => !f.areas.isNullObject);
      loaded.forEach((f) => f.areas.areas.load(["rowIndex", "columnIndex", "formulas", json ? "valuesAsJson" : "values"]));
      if (!loaded.length) return [];
      await context.sync();
      const out: ScopedFormula[] = [];
      for (const { name, areas } of loaded) {
        for (const r of areas.areas.items) {
          const shown: unknown[][] = json ? r.valuesAsJson : r.values;
          shown.forEach((line, i) =>
            line.forEach((v, j) => {
              const f = r.formulas[i][j];
              if (stuckOnName(v) && typeof f === "string" && f.startsWith("="))
                out.push({ sheet: name, row: r.rowIndex + i, col: r.columnIndex + j, formula: f });
            }),
          );
        }
      }
      return out;
    }),

  reenter: (cells) =>
    Excel.run(async (context) => {
      const write = (c: ScopedFormula) => {
        context.workbook.worksheets.getItem(c.sheet).getRangeByIndexes(c.row, c.col, 1, 1).formulas = [[c.formula]];
      };
      let refused = 0;
      for (let i = 0; i < cells.length; i += BATCH) {
        const batch = cells.slice(i, i + BATCH);
        // One recalculation per batch, not one per cell: 39 ms a cell instead of 120 (measured).
        context.application.suspendApiCalculationUntilNextSync();
        batch.forEach(write);
        try {
          await context.sync();
        } catch {
          // A cell Excel refuses (part of an array formula, a protected sheet) stops the
          // batch: one cell at a time.
          for (const c of batch) {
            try {
              write(c);
              await context.sync();
            } catch {
              refused++;
            }
          }
        }
      }
      return refused;
    }),
};
